import CloseIcon from "@mui/icons-material/Close";
import { Box, Drawer, IconButton, Tab, Tabs } from "@mui/material";
import { styled } from "@mui/material/styles";
import React, { useMemo, useState } from "react";
import type { CommandCenterNurse, FloatingNursesRecData } from "../../utils/models";
import Requests from "../RequestData/Requests";
import FloatingNursesTabsContent from "./FloatingNursesTabsContent";

const DrawerHeader = styled("div")(({ theme }) => ({
	display: "flex",
	alignItems: "center",
	padding: theme.spacing(0, 1),
	overflow: "visible",
	...theme.mixins.toolbar,
	justifyContent: "flex-start",
}));

interface IProps {
	open: boolean;
	drawerWidth: number;
	nurses: CommandCenterNurse[];
	stats: Record<string, number>;
	department: string;
	handleDrawerClose: () => void;
	onSubmit: (data: FloatingNursesRecData) => void;
	onDiscard: (nurse: CommandCenterNurse) => void;
	handleSelectDepartment: (departmentId: string) => void;
}

const FloatingNursesRecommendationsDrawer: React.FC<IProps> = ({
	open,
	drawerWidth,
	handleDrawerClose,
	onSubmit,
	onDiscard,
	nurses,
	stats,
	handleSelectDepartment,
	department,
}) => {
	const [currentTab, setCurrentTab] = useState(0);
	const heightDiff = 64;

	const assignments: string[] = useMemo(() => {
		return nurses.flatMap((nurse) => nurse.assignments.map((assignment) => assignment.id).filter((assignment) => assignment !== "Unassigned"));
	}, [nurses]);

	return (
		<Drawer
			sx={{
				width: drawerWidth,
				overflow: "auto",
				color: "primary.dark",
				marginTop: `${heightDiff}px`,
				"& .MuiDrawer-paper": {
					width: drawerWidth,
					height: `calc(100% - ${heightDiff}"px")`,
					marginTop: `${heightDiff}px`,
					borderTop: "1px solid var(--inhouse-neutral)",
					position: "fixed",
					right: open ? 0 : `-${drawerWidth}px`,
					transition: "right 225ms cubic-bezier(0.4, 0, 0.6, 1) 0ms",
					backgroundColor: "background.paper",
				},
			}}
			variant="permanent"
			anchor="right"
			open={open}
		>
			<DrawerHeader>
				<Tabs
					value={currentTab}
					sx={{
						minHeight: "36px",
						"& .MuiTab-root": {
							minHeight: "36px",
							textTransform: "none",
							color: "primary.dark",
							fontSize: "14px",
							padding: "4px 8px",
							minWidth: "auto",
							"&.Mui-selected": {
								color: "primary.dark",
								fontWeight: 600,
							},
						},
						"& .MuiTabs-indicator": {
							backgroundColor: "primary.dark",
						},
					}}
				>
					<Tab
						onClick={() => setCurrentTab(0)}
						label="Float Management"
						icon={<img height={16} width={16} src="/autoawsome.svg" alt="AutoAwesome" />}
						iconPosition="start"
					/>
					<Tab label="Requests" onClick={() => setCurrentTab(1)} />
				</Tabs>
				<IconButton sx={{ p: 0, m: 0, ml: "auto" }} onClick={handleDrawerClose}>
					<CloseIcon sx={{ fontSize: 20 }} />
				</IconButton>
			</DrawerHeader>

			{currentTab === 0 && (
				<Box sx={{ px: 2, py: 0, position: "relative", height: "100%" }}>
					<FloatingNursesTabsContent
						stats={stats}
						nurses={nurses}
						department={department}
						onSubmit={onSubmit}
						onDiscard={onDiscard}
						handleSelectDepartment={handleSelectDepartment}
						isActive={currentTab === 0}
					/>
				</Box>
			)}

			{currentTab === 1 && (
				<Box sx={{ px: 2, py: 0, position: "relative", height: "100%" }}>
					<Requests assignments={assignments} isRequestedTab={true} />
				</Box>
			)}
		</Drawer>
	);
};

export default React.memo(FloatingNursesRecommendationsDrawer);
